/**
 * Calendar-date helpers for SQL datetime fields where only the date matters.
 * SQL Server stores datetime2 at midnight; time and timezone must be ignored on read/write.
 */

/** Parse a calendar date without timezone shifts. */
export function parseDateOnly(value) {
  if (value == null || value === '') return null;

  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) return null;

    const mmddyyyy = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(trimmed);
    if (mmddyyyy) {
      const month = parseInt(mmddyyyy[1], 10) - 1;
      const day = parseInt(mmddyyyy[2], 10);
      const year = parseInt(mmddyyyy[3], 10);
      const parsed = new Date(year, month, day);
      return Number.isNaN(parsed.getTime()) ? null : parsed;
    }

    const isoMatch = trimmed.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (isoMatch) {
      const year = parseInt(isoMatch[1], 10);
      const month = parseInt(isoMatch[2], 10) - 1;
      const day = parseInt(isoMatch[3], 10);
      const parsed = new Date(year, month, day);
      return Number.isNaN(parsed.getTime()) ? null : parsed;
    }
  }

  if (value instanceof Date) {
    if (Number.isNaN(value.getTime())) return null;
    return new Date(value.getUTCFullYear(), value.getUTCMonth(), value.getUTCDate());
  }

  return null;
}

/** Format a date-only value as MM/DD/YYYY. */
export function formatDateOnly(value, emptyValue = '') {
  const date = parseDateOnly(value);
  if (!date) return emptyValue;
  return date.toLocaleDateString('en-US', {
    month: '2-digit',
    day: '2-digit',
    year: 'numeric'
  });
}

/** Datagrid / table date column formatter (MM/DD/YYYY, "-" when empty). */
export function formatGridDate(value) {
  return formatDateOnly(value, '-');
}

/** Extract the calendar year from a date-only value. */
export function getDateOnlyYear(value) {
  const date = parseDateOnly(value);
  return date ? date.getFullYear() : null;
}

/** Start of local calendar day for a date-only or datetime value. */
export function startOfDay(dateValue) {
  const date = parseDateOnly(dateValue);
  if (!date) return null;
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

/** Start of today's local calendar day. */
export function startOfTodayLocal() {
  const today = new Date();
  return new Date(today.getFullYear(), today.getMonth(), today.getDate());
}

/** True when `value` is on or before `compareTo` (calendar days, inclusive). */
export function isDateOnlyOnOrBefore(value, compareTo = startOfTodayLocal()) {
  const date = parseDateOnly(value);
  const compareDate = parseDateOnly(compareTo) || startOfTodayLocal();
  if (!date) return true;
  return date.getTime() <= compareDate.getTime();
}

/** End of local calendar day (for inclusive date-range filters). */
export function endOfDay(dateValue) {
  const date = startOfDay(dateValue);
  if (!date) return null;
  return new Date(date.getFullYear(), date.getMonth(), date.getDate(), 23, 59, 59, 999);
}

/** Normalize form/API date values to YYYY-MM-DD for SQL Server. */
export function toIsoDateString(value) {
  if (value == null || value === '') return null;

  if (value instanceof Date) {
    const parsed = parseDateOnly(value);
    if (!parsed) return null;
    const year = parsed.getFullYear();
    const month = String(parsed.getMonth() + 1).padStart(2, '0');
    const day = String(parsed.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) return null;

    if (/^\d{4}-\d{2}-\d{2}/.test(trimmed)) {
      return trimmed.split('T')[0];
    }

    const slashMatch = trimmed.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
    if (slashMatch) {
      const [, month, day, year] = slashMatch;
      return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
    }

    const parsed = parseDateOnly(trimmed);
    if (parsed) {
      const year = parsed.getFullYear();
      const month = String(parsed.getMonth() + 1).padStart(2, '0');
      const day = String(parsed.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    }

    return null;
  }

  return null;
}

/** Add calendar days to a date-only value and return a new local Date. */
export function addDaysToDateOnly(value, days) {
  const date = parseDateOnly(value);
  if (!date) return null;
  const numDays = Number(days);
  if (!Number.isFinite(numDays)) return null;
  const result = new Date(date);
  result.setDate(result.getDate() + numDays);
  return result;
}
